import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, Mail, MapPin, Phone, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import Footer from '../components/Footer';
import { useSiteContent } from '../hooks/useSiteContent';

import { BACKEND_URL } from '../config/api';

export default function Contact() {
  const navigate = useNavigate();
  const { get } = useSiteContent();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try emailing us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <Building2 className="h-6 w-6 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">OnsiteHand</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary text-sm"
          >
            Sign In
          </button>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <Mail className="h-8 w-8 text-orange-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{get('contact.title', 'Contact Us')}</h1>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {get('contact.subtitle', 'Have a question or need help? We\'re here for you.')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="md:col-span-1 space-y-6">
              {/* Email */}
              <div className="card">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Mail className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">Email Us</h3>
                    <a 
                      href="mailto:support@onsitehand.co.uk" 
                      className="text-orange-600 hover:text-orange-700 text-sm"
                    >
                      support@onsitehand.co.uk
                    </a>
                    <p className="text-xs text-slate-500 mt-1">General enquiries</p>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="card">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">Response Time</h3>
                    <p className="text-sm text-slate-600">
                      We typically respond within 1-2 business days.
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Info */}
              <div className="card" data-testid="contact-company-details">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MapPin className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">Company Details</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      <strong>ONSITEHAND LIMITED</strong><br />
                      Private limited company<br />
                      Registered in England and Wales<br />
                      Company number: <span data-testid="contact-company-number">17186065</span>
                    </p>
                    <p className="text-sm text-slate-600 mt-3" data-testid="contact-registered-office">
                      <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Registered office</span>
                      Unit A449, 4-6 Greatorex Street,<br />
                      London, E1 5NF,<br />
                      United Kingdom
                    </p>
                    <p className="text-xs text-slate-500 mt-3 italic">
                      Our registered office is for official company correspondence only and is not a walk-in customer support address.
                    </p>
                    <p className="text-xs text-slate-500 mt-3" data-testid="contact-companies-house">
                      Official Companies House register:<br />
                      <a
                        href="https://find-and-update.company-information.service.gov.uk/company/17186065"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-600 hover:text-orange-700 underline"
                        data-testid="contact-companies-house-link"
                      >
                        View ONSITEHAND LIMITED
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-2">
              <div className="card">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Message Sent!</h3>
                    <p className="text-slate-600 mb-6">
                      Thanks for contacting us. We'll get back to you as soon as possible.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="text-orange-600 hover:text-orange-700 font-medium"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">Send us a message</h2>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full"
                      >
                        <option value="">Select a topic</option>
                        <option value="General Enquiry">General Enquiry</option>
                        <option value="Technical Support">Technical Support</option>
                        <option value="Billing & Subscriptions">Billing & Subscriptions</option>
                        <option value="Account Help">Account Help</option>
                        <option value="Feedback">Feedback</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="How can we help you?"
                        className="w-full"
                      />
                    </div>

                    {error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full btn-primary flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer minimal />
    </div>
  );
}
